import { FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import { BmExpFg, BmExpGroupFg, BmItemForm } from "../../pojo/bm-form";
import { BmCalcType, BmDataFillingType } from "../../pojo/bm-enums";
import { BmExpGroup, BmPeriodEnum } from "../../pojo/bm-classes";
import { BmCalcHelperService } from "../general/bm-calc-helper.service";
import { ValidatorNotBlank } from "../../pojo/bm-const";

export class BmExpensesCalcService {

  public static recalculateSectionTotal(expensesFg: FormGroup<BmExpFg>): void {
    const groupsFA = expensesFg.controls.groups;
    const grandTotalFA = expensesFg.controls.total;
    const grandTotalRowValue = expensesFg.controls.totalRow;

    if (!groupsFA.length) {
      grandTotalFA.clear();
      grandTotalRowValue.patchValue(0, { emitEvent: false });
      return;
    }

    for (let idx = 0; idx < grandTotalFA.length; idx++) {
      let sum = 0;
      groupsFA.controls.forEach((grp: FormGroup<BmExpGroupFg>) => {
        const v = Number(grp.controls.items.at(idx).getRawValue().itemValue) ?? 0;
        sum += v;
      });
      grandTotalFA.at(idx).patchValue(
        { itemValue: sum, calcType: BmCalcType.GENERATED },
        { emitEvent: false }
      );
    }

    const totalRowValue = BmCalcHelperService.recalculateRowTotal(grandTotalFA);
    expensesFg.controls.totalRow.patchValue(totalRowValue, { emitEvent: false });
  }

  public static recalculateGroupRowTotal(formGroup: FormGroup<BmExpGroupFg>): void {
    const subGroups = formGroup.controls.items;
    const totalRowValue = BmCalcHelperService.recalculateRowTotal(subGroups);
    formGroup.controls.totalRow.patchValue(totalRowValue, { emitEvent: false });
  }

  /**
   * Builds a single Operating Expenses Group form and does an initial recalculation.
   */
  public static createExpGroupForm(group: BmExpGroup): FormGroup<BmExpGroupFg> {
    const subGroupFA = new FormArray<FormGroup<BmItemForm>>(
      (group.items || []).map((data) =>
        BmCalcHelperService.createBmItemForm(data)
      )
    );

    const fg = new FormGroup<BmExpGroupFg>({
      uuid: new FormControl(group.uuid),
      name: new FormControl(group.name, { validators: [ Validators.required, ValidatorNotBlank] }),
      priority: new FormControl(group.priority),
      isRenameMode: new FormControl<boolean>(false),
      oldName: new FormControl(group.name),
      dataFillingType: new FormControl<BmDataFillingType>(group.dataFillingType),
      standardValue: new FormControl<number>(group.standardValue),
      items: subGroupFA,
      totalRow: new FormControl<number>(0),
      totalRowPcnt: new FormControl<number>(0),
    });

    BmCalcHelperService.disableItems(fg.controls.items);

    this.recalculateGroup(fg);
    return fg;
  }

  /**
   * Recalculates the group based on {@link dataFillingType} & {@link standardValue}.
   */
  public static recalculateGroup(fg: FormGroup<BmExpGroupFg>): void {
    const stdVal = fg.controls.standardValue.getRawValue() ?? 0;
    const dataFillingType = fg.controls.dataFillingType.value;

    const itemsByYear = BmCalcHelperService.groupItemsByYear(fg.controls.items);

    itemsByYear.forEach(({ yearFG, quartersFG }) => {
      if (!yearFG) {
        return;
      }
      const isYearManual = (yearFG.value.calcType === BmCalcType.MANUAL);

      switch (dataFillingType) {
        case BmDataFillingType.YEARLY:
          this.applyYearlyFillingType(stdVal, yearFG, quartersFG, isYearManual);
          break;
        case BmDataFillingType.QUARTERLY:
          this.applyQuarterlyFillingType(stdVal, yearFG, quartersFG, isYearManual);
          break;
        case BmDataFillingType.MONTHLY:
          this.applyMonthlyFillingType(stdVal, yearFG, quartersFG, isYearManual);
          break;
      }
    });
  }

  /**
   * Called when user edits any subGroupData control (quarter or year).
   * Enforces the "only one approach per year" logic:
   *   - If user edits a quarter => that quarter -> MANUAL => sum -> year=CALCULATED => disable
   *   - If user edits a year => all quarters -> yearVal/4 => CALCULATED => disable
   */
  public static handleItemsChange(fg: FormGroup<BmExpGroupFg>): void {
    const fa = fg.controls.items;

    fa.controls.forEach((itemFG) => {

      if (!itemFG.dirty) {
        // not changed
        return;
      }
      const period = itemFG.value.period;
      const newVal = itemFG.getRawValue().itemValue ?? 0;
      const quarters = BmCalcHelperService.findQuarterItemsInSelectedYear(fa, period.yearNumber);
      const yearFG = BmCalcHelperService.findYearItemInSelectedYear(fa, period.yearNumber);

      if (period.type === BmPeriodEnum.QUARTER) {
        this.updateYearFromQuarters(yearFG, quarters);
      } else {
        this.updateQuartersFromYear(quarters, newVal);
      }
      itemFG.markAsPristine();

    });
  }

  /**
   * Update values if data filling type was changed to {@link BmDataFillingType.YEARLY}
   *
   * LOGIC:
   *  year has manual value     => do nothing with quarter values in this year.
   *  year has NOT manual value =>
   *    Set in year => {@link stdVal}
   *    Example:
   *
   *       stdVal 2000 fillType Quarterly
   *    Q1 1000 Manual
   *    Q2 500  Manual
   *    Q3 2000 Generated
   *    Q4 2000 Generated
   *    Y  5500 Generated
   *
   *    Change: stdVal 2000 fillType YEARLY
   *    Q1 1000 Manual
   *    Q2 500  Manual
   *    Q3 to be calculated
   *    Q4 to be calculated
   *    Y  2000 Generated
   *    leftOver = 2000 - (1000 + 500) = 500
   *    [Q3,Q4].length = 2 // length of nonManual quarters
   *    Q3 = leftOver/2 = 250
   *    Q4 = leftOver/2 = 250
   *
   *    =>
   *    Q1 1000 Manual
   *    Q2 500  Manual
   *    Q3 250 calculated
   *    Q4 250 calculated
   *    Y  2000 Generated
   *
   * @param stdVal standard Value for this group
   * @param yearFG formGroup of the Year
   * @param quartersFG formGroup array of quarters in selected Year
   * @param yearIsManual flag, to show, that year has manual value
   */
  private static applyYearlyFillingType(
    stdVal: number,
    yearFG: FormGroup<BmItemForm>,
    quartersFG: FormGroup<BmItemForm>[],
    yearIsManual: boolean
  ): void {
    if (yearIsManual) {
      return;
    }
    BmCalcHelperService.patchAndDisable(yearFG, stdVal, BmCalcType.CALCULATED);

    const manualQs = quartersFG.filter((q) => q.value.calcType === BmCalcType.MANUAL);
    const sumManual = BmCalcHelperService.calculateSumOfQuarterValues(manualQs);
    const leftOver = yearFG.getRawValue().itemValue - sumManual;

    // set not manual quarters values
    const nonManualQs = quartersFG.filter((q) => q.value.calcType !== BmCalcType.MANUAL);
    const nonManualQuarterValue = nonManualQs.length ? leftOver / nonManualQs.length : 0;
    nonManualQs.forEach((nonManualQuarter) => {
      BmCalcHelperService.patchAndDisable(nonManualQuarter, nonManualQuarterValue, BmCalcType.CALCULATED);
    });
  }

  /**
   * Update values if data filling type was changed to {@link BmDataFillingType.QUARTERLY}
   *
   * LOGIC:
   *  year has manual value     => do nothing with quarter values in this year
   *  year has NOT manual value => set {@link stdVal} in each quarter. Set in year => quarterSum
   *
   * @param stdVal standard Value for this group
   * @param yearFG formGroup of the Year
   * @param quartersFG formGroup array of quarters in selected Year
   * @param yearIsManual flag, to show, that year has manual value
   */
  private static applyQuarterlyFillingType(
    stdVal: number,
    yearFG: FormGroup<BmItemForm>,
    quartersFG: FormGroup<BmItemForm>[],
    yearIsManual: boolean
  ): void {
    if (yearIsManual) {
      return;
    }
    // set quarters
    quartersFG.forEach((qCtrl) => {
      if (qCtrl.value.calcType === BmCalcType.MANUAL) {
        return;
      }
      BmCalcHelperService.patchAndDisable(qCtrl, stdVal, BmCalcType.CALCULATED);
    });
    // set year
    const quarterSum = BmCalcHelperService.calculateSumOfQuarterValues(quartersFG);
    BmCalcHelperService.patchAndDisable(yearFG, quarterSum, BmCalcType.CALCULATED);
  }


  /**
   * Update values if data filling type was changed to {@link BmDataFillingType.MONTHLY}
   *
   * LOGIC:
   *  year has manual value     => do nothing with quarter values in this year.
   *  year has NOT manual value => set {@link stdVal} * 3 in each quarter. Set in year => quarterSum
   *
   * @param stdVal standard Value for this group
   * @param yearFG formGroup of the Year
   * @param quartersFG formGroup array of quarters in selected Year
   * @param yearIsManual flag, to show, that year has manual value
   */
  private static applyMonthlyFillingType(
    stdVal: number,
    yearFG: FormGroup<BmItemForm>,
    quartersFG: FormGroup<BmItemForm>[],
    yearIsManual: boolean
  ): void {
    if (yearIsManual) {
      return;
    }
    // set quarters
    quartersFG.forEach((quarterFg: FormGroup<BmItemForm>) => {
      if (quarterFg.value.calcType === BmCalcType.MANUAL) {
        return;
      }
      BmCalcHelperService.patchAndDisable(quarterFg, stdVal * 3, BmCalcType.CALCULATED);
    });
    // set year
    const quarterSum = BmCalcHelperService.calculateSumOfQuarterValues(quartersFG);
    BmCalcHelperService.patchAndDisable(yearFG, quarterSum, BmCalcType.CALCULATED);
  }

  /**
   * Update Year value if Quarter value was changed
   *
   * LOGIC:
   * If Quarter was changed (become MANUAL) => Year value should be changed (become CALCULATED and disabled)
   *
   * HOW IT WORKS:
   * Calculate the sum of all quarters and set this value in Year
   */
  private static updateYearFromQuarters(
    yearFG: FormGroup<BmItemForm>,
    quarters: FormGroup<BmItemForm>[],
  ): void {
    const quarterSum = BmCalcHelperService.calculateSumOfQuarterValues(quarters);
    BmCalcHelperService.patchAndDisable(yearFG, quarterSum, BmCalcType.CALCULATED);
  }

  /**
   * Update each Quarter value if Year value was changed
   *
   * LOGIC:
   * If Year was changed (become MANUAL) => Quarter value should be changed (become CALCULATED and disabled)
   *
   * HOW IT WORKS:
   * Set in each quarter => year value / number of quarters
   */
  private static updateQuartersFromYear(
    quarters: FormGroup<BmItemForm>[],
    yearVal: number
  ): void {
    const newQuarterValue = yearVal / quarters.length;
    quarters.forEach((quarterFG: FormGroup<BmItemForm>) => {
      BmCalcHelperService.patchAndDisable(quarterFG, newQuarterValue, BmCalcType.CALCULATED);
    });
  }
}
